import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './About.module.css';
import about1 from '../assets/images/about-1.png';
import about2 from '../assets/images/about-2.png';
import about3 from '../assets/images/about-3.png';
import about4 from '../assets/images/about-4.png';
import about5 from '../assets/images/about-5.png';
import SEO from '../components/SEO';

const About = () => {
  const [counts, setCounts] = useState({
    clients: 0,
    team: 0,
    reviews: 0,
    towns: 0
  });
  const statsRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    const currentRef = statsRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animateCounters = () => {
    const targets = { clients: 400, team: 100, reviews: 500, towns: 20 };
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounts({
        clients: Math.floor(targets.clients * progress),
        team: Math.floor(targets.team * progress),
        reviews: Math.floor(targets.reviews * progress),
        towns: Math.floor(targets.towns * progress)
      });

      if (step >= steps) {
        setCounts(targets);
        clearInterval(timer);
      }
    }, interval);
  };

  return (
    <div className={styles.aboutPage}>
      <SEO
        title="About Us"
        description="Learn about All For You Home Health Care. Licensed and ACHC accredited home health agency serving Southern California with compassionate, professional care since our founding."
        keywords="about us, home health care agency, ACHC accredited, licensed home health care, Southern California, professional caregivers"
        canonicalUrl="https://allforyouhc.com/about"
      />
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <div className={styles.heroTextLeft}>
              <p className={styles.heroTag}>ABOUT US</p>
              <h1 className={styles.heroTitle}>
                Why we love<br />what we do
              </h1>
            </div>
            <div className={styles.heroTextRight}>
              <p className={styles.heroDescription}>
                All For You Home Health Care is a locally operated home health agency
                serving Southern California. We specialize in delivering high-quality
                medical and supportive care directly in the patient's home, with a focus
                on dignity, safety, and long-term well-being.
              </p>
            </div>
          </div>

          <div className={styles.heroImagesWrapper}>
            <div className={`${styles.heroImageBox} ${styles.heroImage1}`}>
              <img src={about1} alt="Compassionate elderly care" />
            </div>
            <div className={`${styles.heroImageBox} ${styles.heroImage2}`}>
              <img src={about2} alt="Professional home health services" />
            </div>
            <div className={`${styles.heroImageBox} ${styles.heroImage3}`}>
              <img src={about3} alt="Quality care at home" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className={styles.statsSection} ref={statsRef}>
        <div className={styles.statsContainer}>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>
                {counts.clients}<span className={styles.statPlus}>+</span>
              </div>
              <div className={styles.statLabel}>Happy clients</div>
            </div>

            <div className={styles.statItem}>
              <div className={styles.statNumber}>
                {counts.team}<span className={styles.statPlus}>+</span>
              </div>
              <div className={styles.statLabel}>Team members</div>
            </div>

            <div className={styles.statItem}>
              <div className={styles.statNumber}>
                {counts.reviews}<span className={styles.statPlus}>+</span>
              </div>
              <div className={styles.statLabel}>Positive reviews</div>
            </div>

            <div className={styles.statItem}>
              <div className={styles.statNumber}>
                {counts.towns}<span className={styles.statPlus}>+</span>
              </div>
              <div className={styles.statLabel}>Town Served</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Mission Section */}
      <section className={styles.missionSection}>
        <div className={styles.container}>
          <div className={styles.missionLayout}>
            <div className={styles.missionLeft}>
              <p className={styles.sectionTag}>OUR MISSION</p>
              <h2 className={styles.missionTitleLarge}>
                Supporting Families Through Reliable, Compassionate Care
              </h2>
              <p className={styles.missionDescription}>
                Our mission is to help patients live safely and comfortably at home
                while supporting families through reliable, compassionate, and clinically
                sound care.
              </p>
            </div>
            <div className={styles.missionRight}>
              <div className={styles.missionCard}>
                <div className={styles.missionIconWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className={styles.missionCardTitle}>Clinical Excellence</h3>
                <p className={styles.missionCardText}>
                  Delivering safe, effective, and evidence-based care through licensed professionals.
                </p>
              </div>

              <div className={styles.missionCard}>
                <div className={styles.missionIconWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className={styles.missionCardTitle}>Compassion First</h3>
                <p className={styles.missionCardText}>
                  Every interaction guided by empathy, understanding, and genuine care.
                </p>
              </div>

              <div className={styles.missionCard}>
                <div className={styles.missionIconWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className={styles.missionCardTitle}>Family Partnership</h3>
                <p className={styles.missionCardText}>
                  Working together with families through open communication and collaboration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className={`${styles.contentSection} ${styles.gray}`}>
        <div className={styles.container}>
          <div className={styles.twoColumnGrid}>
            <div className={styles.textColumn}>
              <p className={styles.sectionTag}>OUR APPROACH</p>
              <h2 className={styles.sectionTitle}>
                Personalized, Hands-On Care
              </h2>
              <p className={styles.sectionText}>
                We take a personalized, hands-on approach to home health care. Every
                patient begins with a thorough assessment, followed by a tailored plan
                designed around medical needs, lifestyle, and family involvement.
              </p>
              <p className={styles.sectionText}>
                Our care teams work closely with physicians, families, and caregivers
                to ensure continuity, clarity, and trust at every stage. We don't just
                provide services—we build relationships based on understanding and respect.
              </p>
            </div>
            <div className={styles.imageColumn}>
              <div className={styles.imageWrapper}>
                <img src={about4} alt="Personalized care approach" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Care Philosophy Section */}
      <section className={styles.philosophySection}>
        <div className={styles.container}>
          <div className={styles.philosophyGrid}>
            <div className={styles.philosophyLeft}>
              <p className={styles.philosophyTag}>OUR PHILOSOPHY</p>
              <h2 className={styles.philosophyTitle}>
                Care Built on<br />Trust and Respect
              </h2>
            </div>
            <div className={styles.philosophyRight}>
              <p className={styles.philosophyText}>
                We treat every patient as an individual, not a diagnosis. Our care
                philosophy is rooted in respect, transparency, and accountability.
              </p>
            </div>
          </div>

          <div className={styles.philosophyValuesWrapper}>
            <div className={styles.philosophyValueItem}>
              <div className={styles.philosophyValueIcon}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className={styles.philosophyValueTitle}>Compassion First</h3>
              <p className={styles.philosophyValueDescription}>
                Every interaction is guided by empathy and understanding.
              </p>
            </div>

            <div className={styles.philosophyValueItem}>
              <div className={styles.philosophyValueIcon}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className={styles.philosophyValueTitle}>Clinical Excellence</h3>
              <p className={styles.philosophyValueDescription}>
                Delivering safe, effective, and evidence-based care.
              </p>
            </div>

            <div className={styles.philosophyValueItem}>
              <div className={styles.philosophyValueIcon}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V19C4 19.55 4.45 20 5 20H19C19.55 20 20 19.55 20 19V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className={styles.philosophyValueTitle}>Family Partnership</h3>
              <p className={styles.philosophyValueDescription}>
                Open communication and collaboration with families.
              </p>
            </div>

            <div className={styles.philosophyValueItem}>
              <div className={styles.philosophyValueIcon}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H13V16H11V12H7V10H11V6H13V10H17V12Z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className={styles.philosophyValueTitle}>Safety & Accountability</h3>
              <p className={styles.philosophyValueDescription}>
                Rigorous standards for safety and documentation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className={`${styles.contentSection} ${styles.gray}`}>
        <div className={styles.container}>
          <div className={styles.twoColumnGrid}>
            <div className={styles.imageColumn}>
              <div className={styles.imageWrapper}>
                <img src={about5} alt="Who we serve" />
              </div>
            </div>
            <div className={styles.textColumn}>
              <p className={styles.sectionTag}>WHO WE SERVE</p>
              <h2 className={styles.sectionTitle}>
                Supporting Those Who Wish to Remain at Home
              </h2>
              <p className={styles.sectionText}>
                We support older adults, individuals recovering from illness or surgery,
                and patients managing chronic or complex conditions who wish to remain
                at home.
              </p>
              <p className={styles.sectionText}>
                Our services are designed to ease transitions, reduce hospital
                readmissions, and improve overall quality of life. Whether it's
                short-term recovery support or ongoing chronic care management, we're
                here to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Standards & Additional Info */}
      <section className={`${styles.contentSection} ${styles.light}`}>
        <div className={styles.container}>
          <div className={styles.centerContent}>
            <p className={styles.sectionTag}>COMMITMENT TO EXCELLENCE</p>
            <h2 className={styles.sectionTitle}>
              Professional Standards & Accessibility
            </h2>
          </div>

          <div className={styles.infoCardsGrid}>
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>Professional Standards</h3>
              <p className={styles.infoCardText}>
                All For You Home Health Care operates in compliance with state and
                federal regulations. Our licensed professionals follow established
                clinical protocols and ethical standards to ensure safe, effective,
                and responsible care in every home we serve.
              </p>
            </div>

            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>Service Area</h3>
              <p className={styles.infoCardText}>
                We provide home health services across Southern California, including
                Los Angeles, Orange, Riverside, San Bernardino, and Ventura counties.
                Serving our local community with pride and dedication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>A Partner You Can Rely On</h2>
            <p className={styles.ctaText}>
              Choosing home health care is a significant decision. Our team is here to
              answer questions, guide families, and provide care you can trust—today
              and in the long term.
            </p>
            <Link to="/contact" className={styles.ctaButton}>
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
